import React from "react";
import useReveal from "@/hooks/useReveal";

/**
 * CarlosEMilenaSection, seção pastoral e galeria de 3 fotos.
 *
 * Publicada abaixo do bloco About em `/sobre` (opção B ratificada).
 * NÃO altera a Home devocional-first.
 *
 * Regras editoriais:
 *  - Sem travessão (— ou –)
 *  - Corpo em alinhamento justificado, `hyphens: none`
 *  - Título Cormorant Garamond, texto Inter
 *  - Paleta: azul-noite #0B1E36, dourado #C5A059, fundo cru #F7F5F0
 *  - Fotos 3:4 (retrato-paisagem), grid 1 coluna mobile / 3 desktop
 */

const PHOTOS = [
  {
    src: "/img/carlos-milena/alianca-viva.png",
    alt: "1ª Conferência Aliança Viva Ágape, Milena e Carlos Medeiros",
    caption: "1ª Conferência Aliança Viva Ágape",
  },
  {
    src: "/img/carlos-milena/discipulado-renascer.png",
    alt: "Encontro do Discipulado Renascer, grupo reunido em comunhão",
    caption: "Discipulado Renascer",
  },
  {
    src: "/img/carlos-milena/circulo-oracao-agape.webp",
    alt: "Círculo de Oração da Paróquia Ágape, comunidade reunida diante da cruz",
    caption: "Círculo de Oração Paróquia Ágape",
  },
];

export default function CarlosEMilenaSection() {
  const revealTop = useReveal();
  const revealGallery = useReveal();

  return (
    <section
      id="carlos-e-milena"
      data-testid="carlos-e-milena-section"
      className="relative bg-white py-20 md:py-28 border-t border-[#0B1E36]/8"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-8">
        <div ref={revealTop} className="reveal">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-[#C5A059]" aria-hidden />
            <span className="text-[0.68rem] uppercase tracking-[0.32em] text-[#C5A059] font-medium">
              Juntos na missão
            </span>
          </div>

          {/* Título */}
          <h2
            data-testid="carlos-e-milena-title"
            className="font-serif-display text-[#0B1E36] text-[2.25rem] md:text-[3rem] lg:text-[3.5rem] leading-[1.05] tracking-[-0.024em] font-medium max-w-3xl"
          >
            Carlos e Milena
          </h2>

          {/* Linha de propósito */}
          <p
            data-testid="carlos-e-milena-purpose"
            className="mt-4 font-serif-display italic text-[1.25rem] md:text-[1.5rem] text-[#0B1E36]/75 leading-[1.4] max-w-2xl"
          >
            Juntos, cuidando de casais e famílias pela Palavra e pelo exemplo.
          </p>

          {/* Corpo do texto, dois parágrafos, alinhamento justificado */}
          <div
            data-testid="carlos-e-milena-body"
            className="mt-10 md:mt-12 max-w-2xl space-y-6 text-[#1F1A17]/85 leading-[1.85] text-[1.05rem] md:text-[1.12rem] font-light text-justify"
            style={{ hyphens: "none", WebkitHyphens: "none", msHyphens: "none" }}
          >
            <p>
              Antes de qualquer título, somos um casal. Foi caminhando lado a
              lado, atravessando alegrias e provações do próprio casamento, que
              Deus foi moldando em nós o desejo de servir a outros lares. Não
              como especialistas que dão respostas prontas, mas como discípulos
              que aprenderam, e ainda aprendem, a confiar na Palavra em cada
              curva do caminho.
            </p>
            <p>
              A missão que abraçamos é simples e exigente ao mesmo tempo. Andar
              perto de casais que buscam restauração, orar com famílias que
              atravessam noites difíceis, sustentar filhos que precisam de
              direção, e discipular os que desejam viver o Evangelho dentro de
              casa. Fazemos isso pela Palavra, porque é ela que corrige e
              consola, e pelo exemplo, porque a fé se transmite quando é vista
              vivida.
            </p>
            <p>
              O que compartilhamos aqui nasce da nossa mesa, do nosso quarto de
              oração e da comunidade que Deus reuniu em torno de nós. Se você
              chegou até esta página buscando alguém que compreenda o peso e a
              beleza de construir uma família cristã, saiba que oramos por
              você. Seja bem-vindo à nossa história, e que ela reforce a sua
              com esperança e coragem.
            </p>
          </div>
        </div>
      </div>

      {/* Galeria, largura maior */}
      <div className="max-w-6xl mx-auto px-6 md:px-8 mt-16 md:mt-20">
        <div ref={revealGallery} className="reveal">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-8 bg-[#C5A059]/60" aria-hidden />
            <span className="text-[0.62rem] uppercase tracking-[0.32em] text-[#0B1E36]/50 font-medium">
              Momentos que testemunham a caminhada
            </span>
          </div>

          <ul
            data-testid="carlos-e-milena-gallery"
            className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
          >
            {PHOTOS.map((p, i) => (
              <li
                key={p.caption}
                data-testid={`carlos-e-milena-photo-${i + 1}`}
                className="flex flex-col"
              >
                <div className="relative w-full aspect-[4/5] overflow-hidden rounded-md bg-[#0B1E36]/5 border border-[#0B1E36]/8">
                  <img
                    src={p.src}
                    alt={p.alt}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                <figcaption
                  data-testid={`carlos-e-milena-caption-${i + 1}`}
                  className="mt-4 text-center italic text-[0.85rem] md:text-[0.9rem] text-[#0B1E36]/60 leading-[1.5]"
                >
                  {p.caption}
                </figcaption>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
